import { inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, race, timer } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { UserData } from '../services/state/user-data';
import { Token } from '../services/token/token';

const AUTH_TIMEOUT_MS = 15000;
const ACCOUNT_SETUP_DELAY_MS = 100;

export function authInitializer() {
  return async () => {
    const userStore = inject(UserData);
    const translocoService = inject(TranslocoService);
    const authService = inject(MsalService);
    const msalBroadcastService = inject(MsalBroadcastService);
    const tokenService = inject(Token);

    try {
      await handleRedirectFlow(authService);
      await waitForMsalReady(msalBroadcastService);
      await processAuthentication(
        authService,
        userStore,
        tokenService,
        translocoService,
      );
    } catch (error) {
      console.error('Authentication initialization failed:', error);
      // Continue with app initialization even if auth fails
    }
  };
}

async function handleRedirectFlow(authService: MsalService): Promise<void> {
  try {
    await firstValueFrom(authService.handleRedirectObservable());
  } catch (error) {
    console.error('Redirect error:', error);
  }
}

async function waitForMsalReady(
  msalBroadcastService: MsalBroadcastService,
): Promise<void> {
  const msalReady$ = msalBroadcastService.inProgress$.pipe(
    filter((status) => status === InteractionStatus.None),
    first(),
    switchMap(() => timer(ACCOUNT_SETUP_DELAY_MS).pipe(map(() => void 0))),
  );

  const timeout$ = timer(AUTH_TIMEOUT_MS).pipe(
    map(() => {
      console.warn(
        'MSAL authentication timeout, proceeding with initialization',
      );
      return void 0;
    }),
  );

  await firstValueFrom(race(msalReady$, timeout$));
}

async function processAuthentication(
  authService: MsalService,
  userStore: UserData,
  tokenService: Token,
  translationService: TranslocoService,
): Promise<void> {
  const activeAccount = ensureActiveAccount(authService);
  const idTokenClaims = activeAccount?.idTokenClaims;

  if (!idTokenClaims) {
    console.warn('No ID token claims found - user may not be authenticated');
    return;
  }

  const { tokenBase, tokenContact, tokenOrganisation, tokenCustomFields } =
    tokenService.extractFromClaims(idTokenClaims);

  if (!tokenBase || !tokenContact || !tokenOrganisation) {
    console.warn(
      'Missing tokens for language initialization - user may not be authenticated',
    );
    return;
  }

  const parsedUserInfo = tokenService.parseAndMerge(
    tokenBase,
    tokenContact,
    tokenOrganisation,
  );
  const permissions = tokenService.parsePermissions(tokenCustomFields);

  userStore.updateUserData(parsedUserInfo);
  userStore.updatePermissions(permissions);

  await firstValueFrom(
    translationService.load(parsedUserInfo.language.toLowerCase()),
  );
}

function ensureActiveAccount(authService: MsalService) {
  let activeAccount = authService.instance.getActiveAccount();

  if (!activeAccount) {
    const allAccounts = authService.instance.getAllAccounts();

    if (allAccounts.length > 0) {
      authService.instance.setActiveAccount(allAccounts[0]);
      activeAccount = allAccounts[0];
      console.debug('Set active account:', activeAccount.username);
    }
  }

  return activeAccount;
}
